import { useState, useEffect } from 'react';
import { fetchAllParticipants } from '../services/apiService';

export const useCachedParticipants = () => {
  const [participants, setParticipants] = useState([]);
  const [participantUserIds, setParticipantUserIds] = useState({});
  const [participantUrlLinks, setParticipantUrlLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch participants only once on mount
  useEffect(() => {
    let active = true;

    const loadParticipants = async () => {
      setLoading(true);
      try {
        const allParticipantsData = await fetchAllParticipants();
        if (!active) return;
        
        const participantNames = allParticipantsData
          .map(p => p.username)
          .filter((v, i, a) => a.indexOf(v) === i) // Remove duplicates
          .sort();
        
        const userIds = {};
        const urlLinks = {};
        allParticipantsData.forEach((user) => {
          const displayName = user.username;
          userIds[displayName] = user.id || user.user_id;
          urlLinks[displayName] = user.id || user.user_id;
        });
        
        setParticipants(participantNames);
        setParticipantUserIds(userIds);
        setParticipantUrlLinks(urlLinks);
        setError(null);
      } catch (err) {
        if (!active) return;
        console.error('Could not fetch participants:', err);
        setError('Could not load participants');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadParticipants();
    return () => { active = false; };
  }, []);

  // Function to manually refetch participants (for add/delete user)
  const refetchParticipants = async () => {
    setLoading(true);
    try {
      const allParticipantsData = await fetchAllParticipants();
      
      const participantNames = allParticipantsData
        .map(p => p.username)
        .filter((v, i, a) => a.indexOf(v) === i) // Remove duplicates
        .sort();
      
      const userIds = {};
      const urlLinks = {};
      allParticipantsData.forEach((user) => {
        const displayName = user.username;
        userIds[displayName] = user.id || user.user_id;
        urlLinks[displayName] = user.id || user.user_id;
      });
      
      setParticipants(participantNames);
      setParticipantUserIds(userIds);
      setParticipantUrlLinks(urlLinks);
      setError(null);
    } catch (err) {
      console.error('Could not refetch participants:', err);
      setError('Could not update participants');
    } finally {
      setLoading(false);
    }
  };

  return {
    participants,
    participantUserIds,
    participantUrlLinks,
    loading,
    error,
    refetchParticipants
  };
};
